import React, { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import MainContent from '../components/MainContent';
import Modal from '../components/Modal';
import InvoiceForm from '../components/finance/InvoiceForm';
import InvoiceFiles, { StatusBadge } from '../components/finance/InvoiceFiles';
import {
  CARD,
  PRIMARY,
  SECONDARY,
  formatAmount,
  formatDateTime,
  periodLabel,
} from '../components/finance/financeText';
import {
  PAGE_SIZE,
  financeError,
  useInvoice,
  useMyInvoices,
} from '../hooks/useFinance';

function Detail({ id, onEdit, onClose }) {
  const { data: invoice, isLoading, error } = useInvoice(id, 'my');
  if (isLoading)
    return (
      <div
        className="h-40 rounded bg-gray-100 animate-pulse"
        aria-label="Loading"
      />
    );
  if (error)
    return (
      <p className="text-sm text-gray-600">
        {financeError(error, "We couldn't load this invoice.")}
      </p>
    );
  return (
    <div className="space-y-4 text-left">
      <div className="flex items-center gap-2">
        <StatusBadge status={invoice.status} />
        <span className="text-sm text-gray-700">
          {formatAmount(invoice.amount, invoice.currency)}
        </span>
      </div>
      {invoice.status === 'rejected' && invoice.status_note && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Rejected: {invoice.status_note}
          <span className="block text-xs">
            Fix it and save to send it back for review.
          </span>
        </div>
      )}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <dt className="text-gray-500">Invoice number</dt>
        <dd className="text-gray-900">{invoice.invoice_number}</dd>
        <dt className="text-gray-500">eTIMS invoice number</dt>
        <dd className="text-gray-900">{invoice.etims_invoice_number}</dd>
        <dt className="text-gray-500">KRA PIN</dt>
        <dd className="font-mono text-gray-900">{invoice.kra_pin}</dd>
        <dt className="text-gray-500">Submitted</dt>
        <dd className="text-gray-900">{formatDateTime(invoice.created_at)}</dd>
        {invoice.reviewed_at && (
          <>
            <dt className="text-gray-500">Reviewed</dt>
            <dd className="text-gray-900">
              {formatDateTime(invoice.reviewed_at)}
            </dd>
          </>
        )}
      </dl>
      <InvoiceFiles scope="my" invoice={invoice} />
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className={SECONDARY}>
          Close
        </button>
        {invoice.editable ? (
          <button
            type="button"
            onClick={() => onEdit(invoice)}
            className={PRIMARY}
          >
            Edit
          </button>
        ) : (
          <span className="self-center text-xs text-gray-500">
            {invoice.status === 'paid'
              ? 'Paid invoices can’t be changed.'
              : 'Approved invoices can’t be changed.'}
          </span>
        )}
      </div>
    </div>
  );
}

// Each administrator's own monthly invoices and eTIMS receipts.
export default function MyInvoices() {
  const [offset, setOffset] = useState(0);
  const [modal, setModal] = useState(null); // {mode: 'new'|'view'|'edit', id?, invoice?}
  const { data, isLoading, error } = useMyInvoices(offset);
  const invoices = data?.invoices ?? [];
  const total = data?.total ?? 0;
  const close = () => setModal(null);

  let title = 'Invoice';
  if (modal?.mode === 'new') title = 'Submit an invoice';
  else if (modal?.mode === 'edit')
    title = `Edit ${periodLabel(modal.invoice.period)} invoice`;
  else if (modal?.mode === 'view')
    title = periodLabel(invoices.find((i) => i.id === modal.id)?.period);

  return (
    <MainContent full>
      <div className="space-y-5 pb-10">
        <header className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              My invoices
            </h1>
            <p className="text-xs text-gray-500">
              Your monthly invoice and KRA eTIMS invoice/receipt for finance.
              Only you and the finance team can see them.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setModal({ mode: 'new' })}
            className={`${PRIMARY} inline-flex items-center gap-1`}
          >
            <AddIcon fontSize="small" />
            Submit an invoice
          </button>
        </header>

        {isLoading && (
          <div className={`${CARD} h-48 animate-pulse`} aria-label="Loading" />
        )}
        {error && (
          <div className={`${CARD} p-8 text-center text-sm text-gray-600`}>
            {financeError(error, "We couldn't load your invoices.")}
          </div>
        )}
        {data && total === 0 && (
          <div className={`${CARD} p-10 text-center text-sm text-gray-600`}>
            You haven&apos;t submitted an invoice yet.
          </div>
        )}
        {data && total > 0 && (
          <section className={`${CARD} overflow-hidden`}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
                  <th className="py-2 pl-5 pr-2 font-medium">Month</th>
                  <th className="px-2 py-2 font-medium">Invoice number</th>
                  <th className="px-2 py-2 text-right font-medium">Amount</th>
                  <th className="py-2 pl-2 pr-5 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => (
                  <tr
                    key={invoice.id}
                    onClick={() => setModal({ mode: 'view', id: invoice.id })}
                    className="cursor-pointer border-b border-gray-100 last:border-0 hover:bg-gray-50"
                  >
                    <td className="py-2.5 pl-5 pr-2 text-gray-900">
                      {periodLabel(invoice.period)}
                    </td>
                    <td className="px-2 py-2.5 text-gray-700">
                      {invoice.invoice_number}
                    </td>
                    <td className="px-2 py-2.5 text-right tabular-nums text-gray-900">
                      {formatAmount(invoice.amount, invoice.currency)}
                    </td>
                    <td className="py-2.5 pl-2 pr-5">
                      <StatusBadge status={invoice.status} />
                      {invoice.status === 'rejected' && invoice.status_note && (
                        <span className="block text-xs text-red-700">
                          {invoice.status_note}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {total > PAGE_SIZE && (
              <div className="flex items-center justify-between border-t border-gray-200 px-5 py-2 text-xs text-gray-500">
                <span>
                  {offset + 1}–{Math.min(offset + invoices.length, total)} of{' '}
                  {total}
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={offset === 0}
                    onClick={() => setOffset(offset - PAGE_SIZE)}
                    className={SECONDARY}
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    disabled={offset + PAGE_SIZE >= total}
                    onClick={() => setOffset(offset + PAGE_SIZE)}
                    className={SECONDARY}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </section>
        )}
      </div>

      <Modal modalOpen={Boolean(modal)} onClose={close} title={title}>
        {modal?.mode === 'new' && (
          <InvoiceForm scope="my" onDone={close} onCancel={close} />
        )}
        {modal?.mode === 'edit' && (
          <InvoiceForm
            scope="my"
            invoice={modal.invoice}
            onDone={close}
            onCancel={close}
          />
        )}
        {modal?.mode === 'view' && (
          <Detail
            id={modal.id}
            onClose={close}
            onEdit={(invoice) => setModal({ mode: 'edit', invoice })}
          />
        )}
      </Modal>
    </MainContent>
  );
}
